import classNames from 'classnames';
import {
  ReactNode,
  ElementType,
  useEffect,
  useRef,
  CSSProperties,
  useState,
  SetStateAction,
  Dispatch,
} from 'react';
import Typography, { TypographyProps } from '../../Typography';
import Button from '../../Button';
import Card from '../../Card';
import styles from '../Accordion.module.scss';
import { useSyncState } from '../../../utils';

export interface AccordionItemProps {
  label: ReactNode;
  labelProps?: TypographyProps<'p'>;
  content: ReactNode;
  contentProps?: TypographyProps<any>;
  labelAs?: ElementType;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
  isOpen?: boolean;
}

function AccordionItem({
  label,
  labelProps,
  content,
  contentProps,
  labelAs,
  setIsOpen,
  isOpen = false,
}: AccordionItemProps) {
  const [_isOpen, _setIsOpen] = useSyncState(isOpen, setIsOpen);
  const [dimensions, setDimensions] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;
    const handler = () => setDimensions(contentRef.current?.offsetHeight ?? 0);
    handler();

    window.addEventListener('resize', handler, true);
    return () => {
      window.removeEventListener('resize', handler, true);
    };
  }, []);

  return (
    <Card backgroundColor="background-1" padding="1.75rem 2.25rem" as="li">
      <div className={styles.header}>
        <Typography
          {...labelProps}
          as={labelProps?.as ?? labelAs ?? 'p'}
          textType="p1"
        >
          {label}
        </Typography>
        <Button
          onClick={() => _setIsOpen(!_isOpen)}
          className={classNames(_isOpen && styles['icon--open'], styles.icon)}
          buttonColor="background-2"
          variant="solid"
        >
          <div className={classNames(styles.line, styles['line--1'])} />
          <div className={classNames(styles.line, styles['line--2'])} />
        </Button>
      </div>
      <div
        style={{ '--accordion-h': dimensions + 'px' } as CSSProperties}
        className={classNames(
          _isOpen && styles['contentFrame--open'],
          styles.contentFrame,
        )}
      >
        <Typography
          {...contentProps}
          as={contentProps?.as ?? (typeof content === 'string' ? 'p' : 'div')}
          className={classNames(contentProps?.className, styles.content)}
          ref={contentRef}
          textType="p1"
        >
          {content}
        </Typography>
      </div>
    </Card>
  );
}

export default AccordionItem;
